import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useFeedback } from "../../../Context/FeedbackContext/FeedbackContext";
import { apiPost } from "../../../utils/apiUtils";
import {
  isSystemModerator,
  isUserModerator,
} from "../../../utils/moderatorUtils";
import { useTranslation } from "react-i18next";
import {
  AnswersType,
  CorrectionResponseType,
  AnswerResponseType,
} from "../types";
import { QuinipoloType } from "../../../types/quinipolo";
import { prepareAnswersForSubmission } from "../utils/answerUtils";
import {
  findMissingAnswers,
  findGoalsMismatchIndicesForQuinipolo,
} from "../utils/validationUtils";
import { LEAGUES_WITH_IMAGE_SHARE_BETA } from "../../../config/leaguesWithImageShare";

export const useAnswerSubmission = (
  answers: AnswersType[],
  quinipolo: QuinipoloType,
  correctingModeOn: boolean,
  editCorrectionModeOn: boolean,
  user: any,
  setMissingAnswerIndices: React.Dispatch<React.SetStateAction<number[]>>,
  setHasAttemptedSubmit: React.Dispatch<React.SetStateAction<boolean>>,
  rowRefs: React.MutableRefObject<(HTMLTableRowElement | null)[]>
) => {
  const navigate = useNavigate();
  const { setFeedback } = useFeedback();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);

  const submitQuinipolo = async () => {
    const isCorrectionMode = correctingModeOn || editCorrectionModeOn;
    const canEnterGoalsPerMatch =
      isCorrectionMode &&
      LEAGUES_WITH_IMAGE_SHARE_BETA.includes(quinipolo.league_id);
    const missing = findMissingAnswers(answers, {
      requireGoalsForTieOnMatches1to14: canEnterGoalsPerMatch,
      requireExactGoalsForMatch15: canEnterGoalsPerMatch,
    });
    const goalsMismatches = canEnterGoalsPerMatch
      ? findGoalsMismatchIndicesForQuinipolo(answers, quinipolo)
      : [];
    const invalidIndices = Array.from(new Set([...missing, ...goalsMismatches]));

    if (invalidIndices.length > 0) {
      setHasAttemptedSubmit(true);
      setMissingAnswerIndices(invalidIndices);
      const message =
        goalsMismatches.length > 0
          ? t("goalsMustMatchWinnerForMatches", {
              matches: invalidIndices.map((i) => i + 1).join(", "),
            })
          : t("missingAnswersForMatches", {
              matches: invalidIndices.map((i) => i + 1).join(", "),
            });
      setFeedback({
        message,
        severity: "warning",
        open: true,
      });

      // Scroll to first invalid row
      const firstMissing = invalidIndices[0];
      const rowEl = rowRefs.current[firstMissing];
      if (rowEl && typeof rowEl.scrollIntoView === "function") {
        rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const answerToSubmit = {
      username: localStorage.getItem("username") ?? user.userData.username,
      quinipoloId: quinipolo.id,
      answers: prepareAnswersForSubmission(answers),
    };

    setLoading(true);

    const isMockCorrection = quinipolo.id === "mock-correction";
    const canCorrect =
      isMockCorrection ||
      (isUserModerator(user.userData.userLeagues, quinipolo.league_id) ||
        isSystemModerator(user.userData.role));

    try {
      if (correctingModeOn && quinipolo.league_id && canCorrect) {
        const response = await apiPost<CorrectionResponseType>(
          `/api/quinipolos/quinipolo/${quinipolo.id}/submit-correction`,
          answerToSubmit
        );
        navigate("/correction-success", {
          state: {
            results: response.results,
            leagueId: response.leagueId || quinipolo.league_id,
            endDate: response.endDate,
            participantsLeaderboard:
              response.participantsLeaderboard || undefined,
            averagePointsThisQuinipolo: response.averagePointsThisQuinipolo,
            mostFailed: response.mostFailed,
            quinipoloId: quinipolo.id,
            matchday: response.matchday,
          },
        });
        setFeedback({
          message: response.message,
          severity: "success",
          open: true,
        });
      } else if (editCorrectionModeOn && quinipolo.league_id && canCorrect) {
        const response = await apiPost<CorrectionResponseType>(
          `/api/quinipolos/quinipolo/${quinipolo.id}/submit-correction-edit`,
          answerToSubmit
        );
        navigate("/correction-success", {
          state: {
            results: response.results,
            leagueId: response.leagueId || quinipolo.league_id,
            endDate: response.endDate,
            participantsLeaderboard:
              response.participantsLeaderboard || undefined,
            averagePointsThisQuinipolo: response.averagePointsThisQuinipolo,
            mostFailed: response.mostFailed,
            quinipoloId: quinipolo.id,
            matchday: response.matchday,
          },
        });
        setFeedback({
          message: response.message,
          severity: "success",
          open: true,
        });
      } else {
        const response = await apiPost<AnswerResponseType>(
          `/api/quinipolos/quinipolo/answers`,
          answerToSubmit
        );
        setFeedback({
          message: response.message,
          severity: "success",
          open: true,
        });
        navigate("/");
      }
    } catch (error: unknown) {
      console.error("Error submitting Quinipolo:", error);
      setLoading(false);

      // Check if error is of type AxiosError
      if (axios.isAxiosError(error)) {
        if (error.response && error.response.status === 409) {
          console.log(error.response);
          setFeedback({
            message: error.response.data,
            severity: "error",
            open: true,
          });
        } else {
          setFeedback({
            message:
              error.response?.data?.message ||
              t("errorSubmittingAnswers"),
            severity: "error",
            open: true,
          });
        }
      } else {
        // Handle non-Axios errors
        setFeedback({
          message: t("unexpectedError"),
          severity: "error",
          open: true,
        });
      }
      return; // Exit early on error
    }

    // Only set loading to false on success
    setLoading(false);
  };

  return {
    submitQuinipolo,
    loading,
  };
};
